import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "./Login";

export default function Register() {
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [devToken, setDevToken] = useState(null);
    const { register } = useAuth();
    const nav = useNavigate();
    const [sp] = useSearchParams();

    // Capture marketing attribution once on mount (persist across form submit)
    useEffect(() => {
        const utm = {
            utm_source:   sp.get("utm_source") || "",
            utm_medium:   sp.get("utm_medium") || "",
            utm_campaign: sp.get("utm_campaign") || "",
            utm_term:     sp.get("utm_term") || "",
            utm_content:  sp.get("utm_content") || "",
            referrer:     document.referrer || "",
            demo:         sp.get("demo") === "1",
            landed_at:    new Date().toISOString(),
        };
        try {
            if (Object.values(utm).some((v) => v && v !== "" && v !== false)) {
                sessionStorage.setItem("forge_utm", JSON.stringify(utm));
            }
        } catch { /* ignore */ }
        const preEmail = sp.get("email");
        if (preEmail) setForm((s) => ({ ...s, email: preEmail }));
    }, [sp]);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let utm = null;
            try { utm = JSON.parse(sessionStorage.getItem("forge_utm") || "null"); } catch { /* ignore */ }
            const payload = { ...form };
            if (utm) payload.marketing_attribution = utm;
            const data = await register(payload);
            toast.success("Account created. Verify your email to continue.");
            if (data.dev_verify_token) {
                setDevToken(data.dev_verify_token);
            } else {
                nav(`/verify-email?email=${encodeURIComponent(form.email)}`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    if (devToken) {
        return (
            <AuthShell testid="page-register-devtoken" title="Verify your email" sub="Email delivery is in placeholder mode — your token is shown below." bgImage="/brand/photography/vision.jpg">
                <div className="border border-arch-white/20 p-6 space-y-4" data-testid="dev-token-panel">
                    <div className="overline text-arch-white/50">Verification token</div>
                    <div className="font-mono text-xs break-all p-3 bg-obsidian/60 text-arch-white" data-testid="dev-verify-token">{devToken}</div>
                    <button
                        onClick={() => nav(`/verify-email?email=${encodeURIComponent(form.email)}&token=${encodeURIComponent(devToken)}&next=${encodeURIComponent("/app/onboarding")}`)}
                        className="w-full bg-navy border border-ice/50 text-arch-white px-6 py-3 text-sm hover:bg-navy/80 uppercase tracking-[0.15em]"
                        data-testid="use-dev-token"
                    >
                        Verify now
                    </button>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell testid="page-register" title="Start your trial" bgImage="/brand/photography/vision.jpg">
            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="first" className="text-arch-white/80">First name</Label>
                        <Input id="first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="register-first" className="mt-2 bg-obsidian/60 border-arch-white/20 text-arch-white" />
                    </div>
                    <div>
                        <Label htmlFor="last" className="text-arch-white/80">Last name</Label>
                        <Input id="last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="register-last" className="mt-2 bg-obsidian/60 border-arch-white/20 text-arch-white" />
                    </div>
                </div>
                <div>
                    <Label htmlFor="email" className="text-arch-white/80">Work email</Label>
                    <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="register-email" className="mt-2 bg-obsidian/60 border-arch-white/20 text-arch-white" />
                </div>
                <div>
                    <Label htmlFor="password" className="text-arch-white/80">Password</Label>
                    <Input id="password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={8} data-testid="register-password" className="mt-2 bg-obsidian/60 border-arch-white/20 text-arch-white" />
                    <div className="text-xs text-arch-white/50 mt-1">Minimum 8 characters</div>
                </div>
                <button type="submit" disabled={loading} className="w-full bg-navy border border-ice/50 text-arch-white px-6 py-3 text-sm hover:bg-navy/80 disabled:opacity-50 uppercase tracking-[0.15em]" data-testid="register-submit">
                    {loading ? "Creating…" : "Create account"}
                </button>
                <div className="text-center pt-4 border-t border-arch-white/10">
                    <Link to="/login" className="text-sm text-ice hover:text-arch-white" data-testid="register-cta-login">
                        Already have an account? Sign in
                    </Link>
                </div>
            </form>
        </AuthShell>
    );
}
